import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SearcherChat from './SearcherChat';
import SearcherList, { Searcher } from './SearcherList'; // Import SearcherList and Searcher interface
import './PlayerDashboard.css'; // Reusing some styles, or create SearcherDashboard.css
import { loQueSeaAFloat } from '../utils/TypeUtils';

interface ChatWindow {
  playerId: string;
  playerName: string;
  gameAddress: string;
  gameDate: string;
  gameTime: string;
}

interface SearcherDashboardProps {
  currentSearcherId: string;
  currentSearcherName: string;
  currentSearcherLatitude: number;
  currentSearcherLongitude: number;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

const SearcherDashboard: React.FC<SearcherDashboardProps> = ({
  currentSearcherId,
  currentSearcherName,
  currentSearcherLatitude,
  currentSearcherLongitude,
}) => {
  const { t } = useTranslation();
  const [openChatWindows, setOpenChatWindows] = useState<ChatWindow[]>([]);
  const [searchers, setSearchers] = useState<Searcher[]>([]); // State to hold searchers for SearcherList
  const [maximizedChatId, setMaximizedChatId] = useState<string | null>(null); // New state to track maximized chat
  const [unreadMessagesCount, setUnreadMessagesCount] = useState<{ [playerId: string]: number }>({}); // New state for unread messages

  const handleCloseChat = useCallback((playerId: string) => {
    setOpenChatWindows((prevWindows) => prevWindows.filter((chat) => chat.playerId !== playerId));
    if (maximizedChatId === playerId) {
      setMaximizedChatId(null);
    }
  }, [maximizedChatId]);

  const handleMaximizeChat = useCallback((chatId: string | null) => {
    setMaximizedChatId(chatId);
    if (chatId) {
      setUnreadMessagesCount(prev => ({ ...prev, [chatId]: 0 })); // Reset unread count when chat is maximized
    }
  }, []);

  const handleNewMessage = useCallback((playerId: string) => {
    setUnreadMessagesCount(prev => ({
      ...prev,
      [playerId]: (prev[playerId] || 0) + 1,
    }));
  }, []);

  // Fetch all searchers for the SearcherList component
  const fetchSearchers = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/searchers`);
      if (response.ok) {
        const data = await response.json();
        const typedSearchers: Searcher[] = data.searchers.map((s: any) => ({
          ...s,
          latitude: loQueSeaAFloat(s.latitude),
          longitude: loQueSeaAFloat(s.longitude),
        }));
        setSearchers(typedSearchers);
      } else {
        console.error('Error fetching searchers:', await response.json());
      }
    } catch (error) {
      console.error('Network error fetching searchers:', error);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchSearchers();
    const interval = setInterval(fetchSearchers, 30000); // Refresh searchers every 30 seconds
    return () => clearInterval(interval);
  }, [fetchSearchers]);

  // Polling for new chat requests from players
  useEffect(() => {
    let isMounted = true;
    let longPollTimeout: NodeJS.Timeout | undefined;

    const pollForNewChatRequests = async () => {
      if (!currentSearcherId) return;

      try {
        console.log(`SearcherDashboard: Initiating long poll for new chat requests for searcher ${currentSearcherId}`);
        const response = await fetch(`${backendUrl}/api/chats/searcher/${currentSearcherId}`);
        const data = await response.json();

        if (isMounted && response.ok && data.newChatRequests && data.newChatRequests.length > 0) {
          console.log('SearcherDashboard: New chat requests received:', data.newChatRequests);
          setOpenChatWindows((prevWindows) => {
            const newRequestsToAdd: ChatWindow[] = data.newChatRequests.filter(
              (newChat: ChatWindow) => !prevWindows.some((chat) => chat.playerId === newChat.playerId)
            );
            newRequestsToAdd.forEach(newChat => handleNewMessage(newChat.playerId));
            return [...prevWindows, ...newRequestsToAdd];
          });
        }
      } catch (error) {
        console.error('SearcherDashboard: Long polling for new chat requests failed:', error);
      } finally {
        if (isMounted) {
          longPollTimeout = setTimeout(pollForNewChatRequests, 3000); // Poll every 3 seconds
        }
      }
    };

    pollForNewChatRequests();

    return () => {
      isMounted = false;
      if (longPollTimeout) {
        clearTimeout(longPollTimeout);
      }
    };
  }, [currentSearcherId, backendUrl]);

  // Fetch existing chat sessions on mount
  useEffect(() => {
    if (!currentSearcherId) return;

    const fetchExistingChatSessions = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/searcher/${currentSearcherId}/chat-sessions`);
        if (response.ok) {
          const data = await response.json();
          if (data.chatSessions && data.chatSessions.length > 0) {
            setOpenChatWindows((prevWindows) => {
              const newSessionsToAdd = data.chatSessions.filter(
                (session: ChatWindow) => !prevWindows.some((chat) => chat.playerId === session.playerId)
              );
              return [...prevWindows, ...newSessionsToAdd];
            });
          }
        } else {
          console.error('Error fetching existing chat sessions:', await response.json());
        }
      } catch (error) {
        console.error('Network error fetching existing chat sessions:', error);
      }
    };

    fetchExistingChatSessions();
  }, [currentSearcherId, backendUrl]);

  return (
    <div className="searcher-dashboard-container" style={{
      position: 'fixed',
      bottom: 0,
      right: 0,
      padding: '10px',
      zIndex: 1000,
    }}>
      {/* Render open chat windows */}
      <div className="searcher-chat-windows-container" style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        justifyContent: 'flex-end'
      }}>
        {openChatWindows.map((chat) => (
          <SearcherChat
            key={chat.playerId}
            searcherId={currentSearcherId}
            recipientId={chat.playerId}
            searcherLatitude={currentSearcherLatitude}
            searcherLongitude={currentSearcherLongitude}
            searcherName={currentSearcherName}
            gameAddress={chat.gameAddress}
            gameDate={chat.gameDate}
            gameTime={chat.gameTime}
            onCloseChat={() => handleCloseChat(chat.playerId)}
            isMaximized={maximizedChatId === chat.playerId}
            onMaximizeToggle={handleMaximizeChat}
            unreadCount={unreadMessagesCount[chat.playerId] || 0}
            onNewMessage={handleNewMessage}
          />
        ))}
      </div>
    </div>
  );
};

export default SearcherDashboard;